import React, { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import TypeWriter from '../components/TypeWriter';
import './SignLanguage.css';

// These constants are no longer strictly necessary for client-side processing,
// but we keep them for UI display consistency.
const MIN_STABLE_FRAMES = 8;
const CONFIDENCE_THRESHOLD = 0.6; 

const SignLanguage = () => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const webcamCardRef = useRef(null);
    const outputCardRef = useRef(null);
    const [isDetectionActive, setIsDetectionActive] = useState(false);
    const [isWebcamActive, setIsWebcamActive] = useState(false);
    const [detectedGesture, setDetectedGesture] = useState('');
    const [detectedText, setDetectedText] = useState('');
    const [recentPredictions, setRecentPredictions] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confidence, setConfidence] = useState(0);
    const [gestureStableCount, setGestureStableCount] = useState(0);
    const [lastGesture, setLastGesture] = useState('');
    const [nextDetected, setNextDetected] = useState(false);
    const socketRef = useRef(null);
    const intervalRef = useRef(null);
    const location = useLocation();

    // Force equal height for both cards on desktop
    useEffect(() => {
        function setEqualCardHeights() {
            if (window.innerWidth > 900 && webcamCardRef.current && outputCardRef.current) {
                const webcamHeight = webcamCardRef.current.offsetHeight;
                outputCardRef.current.style.height = webcamHeight + 'px';
            } else if (outputCardRef.current) {
                outputCardRef.current.style.height = 'auto';
            }
        }
        setEqualCardHeights();
        window.addEventListener('resize', setEqualCardHeights);
        return () => window.removeEventListener('resize', setEqualCardHeights);
    }, []);

    // Socket.IO connection and event handling
    useEffect(() => {
        // Connect to the HF Space backend server
        socketRef.current = io('https://bharathchilaka-test.hf.space', {
            transports: ['websocket', 'polling'],
            cors: {
                origin: '*',
                methods: ['GET', 'POST']
            }
        });

        socketRef.current.on('connect', () => {
            console.log('Connected to HF Space server via Socket.IO');
            setSuccess('Connected to Sign Language Recognition Server');
        });

        socketRef.current.on('disconnect', () => {
            console.log('Disconnected from server');
            setError('Disconnected from server');
        });

        // Event listener for prediction results from the server
        socketRef.current.on('prediction_result', (data) => {
            const pred = data.prediction_data;

            // Update state with data from the server
            setDetectedGesture(pred.predicted_character || '');
            setConfidence(1); // Confidence is handled server-side, assume 1 for a stable prediction
            setGestureStableCount(pred.stability_count);
            setLastGesture(pred.last_gesture || '');
            setDetectedText(pred.displayed_text || '');
            
            // Draw the processed image from the server onto the canvas
            if (data.processed_image && canvasRef.current) {
                const img = new Image();
                img.onload = () => {
                    const ctx = canvasRef.current.getContext('2d');
                    canvasRef.current.width = img.width;
                    canvasRef.current.height = img.height;
                    ctx.drawImage(img, 0, 0);
                };
                img.src = data.processed_image;
            }
        });

        socketRef.current.on('recent_predictions_updated', (data) => {
            setRecentPredictions(data.predictions);
        });

        socketRef.current.on('text_cleared', (data) => {
            setDetectedText(data.text);
            setSuccess('Text cleared');
        });
        
        socketRef.current.on('speak_text', (data) => {
            if ('speechSynthesis' in window) {
                const utterance = new SpeechSynthesisUtterance(data.text);
                speechSynthesis.speak(utterance);
                setSuccess('Speaking text...');
            } else {
                setError('Text-to-speech not supported in this browser.');
            }
        });

        return () => {
            if (socketRef.current) socketRef.current.disconnect();
            if (intervalRef.current) clearInterval(intervalRef.current);
            stopRealtimeDetection();
            stopWebcam();
        };
    }, []);

    // Enhanced cleanup on unmount and route change
    useEffect(() => {
        return () => {
            // Ensure all detection is fully stopped
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            
            // Stop webcam and clean up streams
            stopRealtimeDetection();
            stopWebcam();
            
            // Stop any active webcam streams globally
            const videoElements = document.querySelectorAll('video');
            videoElements.forEach(videoElement => {
                if (videoElement && videoElement.srcObject) {
                    const tracks = videoElement.srcObject.getTracks();
                    tracks.forEach(track => track.stop());
                    videoElement.srcObject = null;
                }
            });
            
            // Clear any remaining intervals
            const maxIntervalId = setInterval(() => {}, 0);
            for (let i = 0; i < maxIntervalId; i++) {
                clearInterval(i);
            }
            
            // Ensure socket is disconnected
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, []);

    // Cleanup on route change
    useEffect(() => {
        const currentPath = location.pathname;
        return () => {
            if (currentPath !== '/sign-language') {
                try {
                    // Stop detection and webcam when navigating away
                    stopRealtimeDetection();
                    stopWebcam();
                    
                    // Ensure socket is disconnected
                    if (socketRef.current) {
                        socketRef.current.disconnect();
                        socketRef.current = null;
                    }
                    
                    // Force cleanup of all video elements
                    const videoElements = document.querySelectorAll('video');
                    videoElements.forEach(videoElement => {
                        if (videoElement && videoElement.srcObject) {
                            const tracks = videoElement.srcObject.getTracks();
                            tracks.forEach(track => {
                                try {
                                    track.stop();
                                } catch (e) {
                                    console.log('Error stopping track on route change:', e);
                                }
                            });
                            videoElement.srcObject = null;
                        }
                    });
                    
                } catch (error) {
                    console.log('Error during route cleanup:', error);
                }
            }
        };
    }, [location.pathname, isDetectionActive, isWebcamActive]);

    const clearAll = () => {
        setDetectedText('');
        setDetectedGesture('');
        setRecentPredictions([]);
        setConfidence(0);
        setGestureStableCount(0);
        setLastGesture('');
        setNextDetected(false);
        setSuccess('All data cleared');
        // Tell the server to clear its state as well
        if (socketRef.current) {
            socketRef.current.emit('clear_text');
            socketRef.current.emit('clear_recent_predictions');
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(detectedText).then(() => {
            setSuccess('Copied to clipboard!');
            setTimeout(() => setSuccess(''), 2000);
        }).catch(err => {
            setError('Failed to copy: ' + err.message);
        });
    };

    const clearRecentPredictions = () => {
        setRecentPredictions([]);
        setSuccess('Recent predictions cleared');
        if (socketRef.current) {
            socketRef.current.emit('clear_recent_predictions');
        }
    };

    const startRealtimeDetection = async () => {
        try {
            await startWebcam();
            setIsDetectionActive(true);
            setSuccess('Detection started! Show hand gestures clearly in front of the camera.');

            intervalRef.current = setInterval(() => {
                if (videoRef.current && videoRef.current.videoWidth > 0 && socketRef.current && socketRef.current.connected) {
                    try {
                        if (!videoRef.current || !socketRef.current || !socketRef.current.connected) {
                            return;
                        }
                        
                        const video = videoRef.current;
                        if (!video.videoWidth || !video.videoHeight) {
                            return;
                        }
                        
                        const canvas = document.createElement('canvas');
                        if (!canvas || !canvas.getContext) {
                            return;
                        }
                        
                        const ctx = canvas.getContext('2d');
                        if (!ctx) {
                            return;
                        }
                        
                        try {
                            canvas.width = video.videoWidth;
                            canvas.height = video.videoHeight;
                            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                            const imageData = canvas.toDataURL('image/jpeg', 0.8);
                            socketRef.current.emit('process_frame', { image: imageData });
                        } catch (error) {
                            console.log('Error processing frame:', error);
                            if (intervalRef.current) {
                                clearInterval(intervalRef.current);
                                intervalRef.current = null;
                            }
                        }
                    } catch (error) {
                        console.log('Error in interval processing:', error);
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                            intervalRef.current = null;
                        }
                    }
                }
            }, 100); // Send a frame to the server every 100ms (10 FPS)

        } catch (err) {
            setError('Error starting detection: ' + err.message);
            setIsDetectionActive(false);
        }
    };

    const stopRealtimeDetection = () => {
        try {
            setIsDetectionActive(false);
            setSuccess('Real-time detection stopped');
            
            // Clear interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            
            // Stop webcam
            stopWebcam();
            
            // Clear canvas
            if (canvasRef.current) {
                try {
                    const ctx = canvasRef.current.getContext('2d');
                    if (ctx) {
                        ctx.clearRect(0, 0, canvasRef.current.width || 640, canvasRef.current.height || 480);
                    }
                } catch (e) {
                    console.log('Error clearing canvas:', e);
                }
            }
            
            // Stop all active webcam streams globally
            const videoElements = document.querySelectorAll('video');
            videoElements.forEach(videoElement => {
                if (videoElement && videoElement.srcObject) {
                    const tracks = videoElement.srcObject.getTracks();
                    tracks.forEach(track => {
                        try {
                            track.stop();
                        } catch (e) {
                            console.log('Error stopping track:', e);
                        }
                    });
                    videoElement.srcObject = null;
                }
            });
            
        } catch (error) {
            console.log('Error in stopRealtimeDetection:', error);
        }
    };

    const startWebcam = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480 }
            });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                setIsWebcamActive(true);
            }
        } catch (err) {
            setError('Error accessing webcam: ' + err.message);
        }
    };

    const stopWebcam = () => {
        try {
            if (videoRef.current && videoRef.current.srcObject) {
                const tracks = videoRef.current.srcObject.getTracks();
                tracks.forEach(track => {
                    try {
                        track.stop();
                    } catch (e) {
                        console.warn('Error stopping track:', e);
                    }
                });
                videoRef.current.srcObject = null;
                setIsWebcamActive(false);
            }
            
            // Ensure all video elements are cleaned up
            const videoElements = document.querySelectorAll('video');
            videoElements.forEach(videoElement => {
                if (videoElement && videoElement.srcObject) {
                    const tracks = videoElement.srcObject.getTracks();
                    tracks.forEach(track => {
                        try {
                            track.stop();
                        } catch (e) {
                            console.log('Error stopping global track:', e);
                        }
                    });
                    videoElement.srcObject = null;
                }
            });
            
        } catch (error) {
            console.log('Error in stopWebcam:', error);
        }
    };

    return (
        <div className="sign-language-page">
            <div className="container">
                <div className="sign-language-header card" data-aos="fade-up" data-aos-duration="900">
                    <h1>
                        <TypeWriter 
                            text="🤟 Sign Language Recognition" 
                            speed={80} 
                            cursorColor="white"
                        />
                    </h1>
                    <p>Use your webcam to recognize ASL gestures in real-time</p>
                </div>

                <div className="sign-flex-row">
                    <div className="webcam-section card" ref={webcamCardRef} data-aos="zoom-in" data-aos-delay="100" data-aos-duration="800">
                        <div className="webcam-header">
                            <h2 className="sign-language-section-heading">Gesture Recognition</h2>
                            <div className="webcam-controls">
                                <button
                                    className={`realtime-btn${isDetectionActive ? ' stop' : ''}`}
                                    onClick={isDetectionActive ? stopRealtimeDetection : startRealtimeDetection}
                                >
                                    {isDetectionActive ? '⏹️ Stop Real-time Detection' : '🔄 Start Detection'}
                                </button>
                            </div>
                        </div>

                        <div className="webcam-container" data-aos="fade-up" data-aos-delay="150" data-aos-duration="700" style={{ position: 'relative' }}>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                width={640}
                                height={480}
                                className="webcam-video"
                                style={{ borderRadius: '16px', background: '#000', display: isWebcamActive ? 'block' : 'none' }}
                            />
                            <canvas
                                ref={canvasRef}
                                className="canvas-overlay"
                                style={{
                                    display: isWebcamActive ? 'block' : 'none',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    width: '100%',
                                    height: '100%',
                                    pointerEvents: 'none',
                                    zIndex: 2
                                }}
                            />
                        </div>

                        {(detectedGesture || isDetectionActive) && (
                            <div className="gesture-result" data-aos="fade-in" data-aos-delay="200">
                                {detectedGesture ? (
                                    <p>Detected Gesture: <strong>{detectedGesture}</strong> (Confidence: {confidence * 100}%)</p>
                                ) : (
                                    <p>Real-time detection active - Show your hands to see gestures</p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="text-display-section card" ref={outputCardRef} data-aos="zoom-in" data-aos-delay="200" data-aos-duration="800">
                        <h2 className="sign-language-section-heading">Detected Text</h2>
                        <div className="text-container" style={{ flex: 1 }}>
                            <textarea
                                value={detectedText}
                                readOnly
                                className="detected-text-output"
                                placeholder="Detected letters will appear here..."
                                rows="4"
                            />
                            <div className="current-gesture-buffer">
                                <strong>Current Gesture:</strong> {detectedGesture}
                                {detectedGesture && detectedGesture !== 'Next' && (
                                    <span style={{ color: detectedGesture === 'Backspace' ? 'red' : 'green', marginLeft: 8 }}>
                                        {detectedGesture === 'Backspace' ? " (Show 'Next' to delete)" : " (Show 'Next' to add)"}
                                    </span>
                                )}
                            </div>
                            <div className="text-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <button
                                    className="btn btn-primary copy-btn"
                                    onClick={copyToClipboard}
                                    disabled={!detectedText}
                                >
                                    📋 Copy Text
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    style={{ background: '#dc3545', color: 'white', border: 'none', fontWeight: 'bold' }}
                                    onClick={clearAll}
                                    disabled={!detectedText && !detectedGesture && !gestureStableCount}
                                >
                                    🗑️ Clear All
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={() => socketRef.current.emit('speak_text')}
                                    disabled={!detectedText}
                                >
                                    🔊 Speak
                                </button>
                            </div>
                        </div>
                        <div className="debug-status" style={{ marginTop: 10, fontSize: '0.95em', color: '#333', background: '#f8f9fa', borderRadius: 8, padding: 8 }}>
                            <div><strong>Status:</strong> {success || error || 'Ready'}</div>
                            <div><strong>Stable:</strong> {gestureStableCount}/{MIN_STABLE_FRAMES} | <strong>Confidence:</strong> {(confidence * 100).toFixed(1)}%</div>
                            <div><strong>Last Gesture:</strong> {lastGesture}</div>
                        </div>
                    </div>
                </div>

                <div className="recent-predictions-section card" data-aos="zoom-in" data-aos-delay="250" data-aos-duration="800">
                    <h2 className="sign-language-section-heading">📊 Recent Predictions</h2>
                    <div className="predictions-container">
                        {recentPredictions.length > 0 ? (
                            <div className="predictions-list">
                                {recentPredictions.map((prediction, index) => (
                                    <div key={index} className="prediction-item" data-aos="fade-up" data-aos-delay={300 + index * 50}>
                                        <div className="prediction-gesture">
                                            <span className="gesture-letter">{prediction.character}</span>
                                        </div>
                                        <div className="prediction-details">
                                            <span className="prediction-time">{prediction.timestamp}</span>
                                            <span className="prediction-date">{prediction.date}</span>
                                        </div>
                                        <div className="prediction-number">#{index + 1}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-predictions" data-aos="fade-up" data-aos-delay="300">
                                <p>No predictions yet. Start detecting gestures to see recent predictions!</p>
                            </div>
                        )}
                        {recentPredictions.length > 0 && (
                            <div className="predictions-actions">
                                <button
                                    className="btn btn-secondary"
                                    onClick={clearRecentPredictions}
                                >
                                    🗑️ Clear Predictions
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="instructions-section card" data-aos="fade-up" data-aos-delay="300" data-aos-duration="900">
                    <h2 className="sign-language-section-heading">How to Use</h2>
                    <div className="instructions-grid">
                        <div className="instruction-item" data-aos="fade-right" data-aos-delay="350">
                            <div className="instruction-icon">📹</div>
                            <h4>Start Detection</h4>
                            <p>Click "Start Detection" to begin streaming from your camera</p>
                        </div>
                        <div className="instruction-item" data-aos="fade-up" data-aos-delay="400">
                            <div className="instruction-icon">🫟</div>
                            <h4>Make Gestures</h4>
                            <p>Position your hands clearly in front of the camera</p>
                        </div>
                        <div className="instruction-item" data-aos="fade-up" data-aos-delay="450">
                            <div className="instruction-icon">👁️</div>
                            <h4>Confirm with 'Next'</h4>
                            <p>Hold the "Next" gesture to add the detected sign to the text</p>
                        </div>
                        <div className="instruction-item" data-aos="fade-left" data-aos-delay="500">
                            <div className="instruction-icon">📋</div>
                            <h4>View Results</h4>
                            <p>See detected gestures and text in the output section</p>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="message error fade-in" data-aos="fade-in" data-aos-delay="600">
                        {error}
                    </div>
                )}
                
                {success && (
                    <div className="message success fade-in" data-aos="fade-in" data-aos-delay="650">
                        {success}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SignLanguage;